<template>
	<section v-loading="loading" class="image-upload" :style="{ width: `${width}px`, 'min-height': `${height}px` }">

        <vue-transmit
            ref="uploader"
            v-bind="uploaderConfig"
            :accepted-file-types="acceptedFileTypes"
            @added-file="onFileAdded"
        >

            <div v-show="!local.src || fixedUploader">
                <slot name="uploader">
                    <div class="iu-uploader">
                        <div class="iu-uploader-icon">
                            <icon src="cloud_upload" />
                        </div>
                        <div class="iu-uploader-body">
                            <div class="iu-uploader-instructions-title">
                                Drop image to upload, or <a class="text-link" @click="browse">browse</a>
                            </div>
                            <div v-if="!customSize" class="iu-uploader-instructions-subtitle">
                                Recommended size: {{croppedWidth}}x{{croppedHeight}}
                            </div>
                        </div>
                    </div>
                </slot>
            </div>

            <div v-show="local.src || fixedUploader">
                <canvas
                    ref="cropperCanvas"
                    :width="width"
                    :height="height"
                    :class="cropperCssClasses"
                    @dragover.prevent
                    @mousedown="onCropperDragStart"
                    @touchstart="onCropperDragStart"
                    @wheel="onCropperWheel"
                />

                <div class="image-uploader-cropper-toolbar">
                    <div class="toolbar toolbar-detached">
                        <div class="toolbar-row">
                            <ul v-if="!fixedUploader">
                                <li v-if="!realtime"><btn link icon="check" title="Save" @click="save" /></li>
                                <li v-if="!realtime"><btn link icon="clear" title="Clear" @click="clear" /></li>
                                <li v-if="realtime"><btn link icon="folder_open" title="Upload a new image" @click="browse" /></li>
                            </ul>
                            <ul>
                                <li><btn link icon="zoom_in" title="Zoom in" @click="zoomIn" /></li>
                                <li><btn link icon="zoom_out" title="Zoom out" @click="zoomOut" /></li>
                            </ul>
                            <ul v-if="!disableRotate">
                                <li><btn link icon="rotate_right" title="Rotate right" @click="rotateRight" /></li>
                                <li><btn link icon="rotate_left" title="Rotate left" @click="rotateLeft" /></li>
                            </ul>
                            <ul v-if="customSize">
                                <li>Width&nbsp;<input title="Width" placeholder="Width" v-model="croppedWidth" @change="redraw" /></li>
                                <li>Height&nbsp;<input title="Height" placeholder="Height" v-model="croppedHeight" @change="redraw" /></li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

        </vue-transmit>


    </section>
</template>


<script>
    import Vue from 'vue';
    import _ from 'lodash';
    import VueTransmit from 'vue-transmit';
    import { AsyncMixin, LocalPropsMixin } from 'pdk/mixins';
    import { notifications } from 'pdk/services';


    // Install vue-transmit
    Vue.use(VueTransmit);


	export default {
        name: 'image-upload',
        mixins: [AsyncMixin, LocalPropsMixin({
            src: {
                type: String,
                default: null
            },
            scale: {
                type: Number,
                default: 1
            },
            rotation: {
                type: Number,
                default: 0
            }
        })],
        props: {
            accept: {
                type: String,
                default: 'image/*'
            },
            borderRadius: {
                type: Number,
                default: 0
            },
            croppedWidth: {
                type: Number,
                default: 260
            },
            croppedHeight: {
                type: Number,
                default: 160
            },
            customSize: {
                type: Boolean,
                default: false
            },
            disableRotate: {
                type: Boolean,
                default: false
            },
            fixedUploader: {
                type: Boolean,
                default: false
            },
            height: {
                type: Number,
                default: 200
            },
            maxScale: {
                type: Number,
                default: 10
            },
            realtime: {
                type: Boolean,
                default: false
            },
            width: {
                type: Number,
                default: 300
            }
        },
        data() {
            return {
                uploaderConfig: {
                    url: '',
                    clickable: false,
                    createImageThumbnails: false,
                    autoQueue: false,
                    autoProcessQueue: false
                },
                cropper: {
                    canvas: null,
                    context: null,
                    state: {
                        cursor: 'default',
                        drag: false,
                        my: null,
                        mx: null,
                        xxx: 'ab',
                        image: {
                            x: 0,
                            y: 0,
                            resource: null,
                            loaded: false
                        }
                    }
                }
            };
        },
        computed: {
            acceptedFileTypes() {
                return this.accept.split(',');
            },
            horizontalBorder() {
                if (this.croppedWidth >= this.width) return this.width;
                return (this.width - this.croppedWidth) / 2;
            },
            verticalBorder() {
                if (this.croppedHeight >= this.height) return this.height;
                return (this.height - this.croppedHeight) / 2;
            },
            rotationRadian() {
                return this.local.rotation * Math.PI / 180;
            },
            cropperCssClasses() {
                const classes = {};

                classes[`iu-cursor-${this.cropper.state.cursor}`] = true;

                return classes;
            }
        },
        watch: {
            'cropper.state': {
                deep: true,
                handler() {
                    if (this.cropper.state.image.loaded) {
                        this.redraw();
                    }
                }
            },
            'local.src'(value) {
                if(value) this.loadImage(value);
            },
            'local.scale' () {
                if (this.cropper.state.image.loaded) {
                    this.replaceImageInBounds();
                    this.redraw();
                }
            },
            'local.rotation'() {
                if (this.cropper.state.image.loaded) {
                    this.replaceImageInBounds();
                    this.redraw();
                }
            },
            borderRadius () {
                this.redraw();
            }
        },
        mounted() {
            this.cropper.canvas = this.$refs.cropperCanvas;
            this.cropper.context = this.cropper.canvas.getContext('2d');
            this.paint();

            if (this.local.src) {
                this.loadImage(this.local.src);
            }
        },
        methods: {
            browse() {
                this.$refs.uploader.triggerBrowseFiles();
            },
            clear() {
                this.local.src = undefined;
            },
            save() {
                this.emitChange();
            },
            getDataUrl(file) {
                return new Promise((resolve, reject) => {
                    try {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                            resolve(event.target.result);
                        };
                        reader.readAsDataURL(file._nativeFile);
                    }
                    catch (e) {
                        reject(e);
                    }
                });
            },
            loadImage(url) {
                const imageObj = new Image();
                this.beginAsync();

                imageObj.onload = () => {
                    const imageState = this.getInitialSize(imageObj.width, imageObj.height);
                    this.local.scale = 1;
                    this.local.rotation = 0;
                    this.cropper.state.image.x = 0;
                    this.cropper.state.image.y = 0;
                    this.cropper.state.image.resource = imageObj;
                    this.cropper.state.image.width = imageState.width;
                    this.cropper.state.image.height = imageState.height;
                    this.cropper.state.drag = false;
                    this.$emit('load', this.local.scale);
                    this.cropper.state.image.loaded = true;
                    this.cropper.state.cursor = 'grab';

                    this.endAsync();
                };
                imageObj.onerror = (err) => {
                    console.log('error loading image: ', err);
                    this.endAsync();
                };

                if (!this.isDataUrl(url)) {
                    imageObj.crossOrigin = 'anonymous';
                }

                imageObj.src = url;
            },
            rotateLeft() {
                this.rotate(-1);
            },
            rotateRight() {
                this.rotate(1);
            },
            rotate(direction) {
                const rotations = [0, 90, 180, 270];
                const index = rotations.indexOf(this.local.rotation);
                let newIndex;

                if (direction == 1) {
                    newIndex = (index == rotations.length - 1) ? 0 : index + 1;
                }
                if (direction == -1) {
                    newIndex = (index == 0) ? rotations.length - 1 : index - 1;
                }

                this.local.rotation = rotations[newIndex];
            },
            zoomIn() {
                this.zoom(1);
            },
            zoomOut() {
                this.zoom(-1);
            },
            zoom(direction) {
                const increment = 0.2;

                const scalingUp = direction > 0;
                let newScale = this.local.scale;

                if (scalingUp) {
                    if(this.local.scale <= this.maxScale - (increment * 2)) newScale += increment;
			    } else {
                    if (this.local.scale >= increment * 2) newScale -= increment;
                }

                this.local.scale = Math.round(newScale * 10) / 10;
            },

            // Canvas helpers
            redraw() {
                // Check for potential bounds error
                if (this.customSize) {
                    const widthExceeded = this.croppedWidth >= this.width;
                    const heightExceeded = this.croppedHeight >= this.height;
                    
                    if (widthExceeded || heightExceeded) {
                        this.croppedWidth = widthExceeded ? this.width - 1 : this.croppedWidth;
                        this.croppedHeight = heightExceeded ? this.height - 1 : this.croppedHeight;
                        notifications.open({
                            content: 'The maximum bounds allowed have been exceeded.'
                        });
                        return;
                    }                    
                }

                const ctx = this.cropper.context;

                ctx.clearRect(0, 0, this.width, this.height);
                this.paint();
                this.paintImage(ctx, this.cropper.state.image, this.horizontalBorder, this.verticalBorder);

                if(this.realtime) this.emitChange();
            },
            paint() {
                const ctx = this.cropper.context;
                ctx.save();

                ctx.translate(0, 0);
                ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';

                let borderRadius = this.borderRadius;
                const horizonalBorderSize = this.horizontalBorder;
                const verticalBorderSize = this.verticalBorder;
                const width = this.width;
                const height = this.height;

                // clamp border radius between zero (perfect rectangle) and half the size without borders (perfect circle or "pill")
                borderRadius = Math.max(borderRadius, 0);
                borderRadius = Math.min(borderRadius, width / 2 - horizonalBorderSize, height / 2 - verticalBorderSize);

                ctx.beginPath();

                // inner rect, possibly rounded
                this.drawRoundedRect(
                    ctx,
                    horizonalBorderSize,
                    verticalBorderSize,
                    width - horizonalBorderSize * 2,
                    height - verticalBorderSize * 2,
                    borderRadius);

                ctx.rect(width, 0, -width, height); // outer rect, drawn "counterclockwise"
                ctx.fill('evenodd');
                ctx.restore();
            },
            paintImage(context, image, horizontalBorder, verticalBorder) {
                if (!image.resource) return;

                var position = this.calculateInitialPosition(image, horizontalBorder, verticalBorder);

                context.save();
                context.globalCompositeOperation = 'destination-over';
                context.translate(this.width / 2, this.height / 2);
                context.rotate(this.rotationRadian);
                context.translate(-this.width / 2, -this.height / 2);
                context.drawImage(
                    image.resource,
                    position.x,
                    position.y,
                    position.width,
                    position.height);
                context.restore();

                // Add a transparency grid
                const img = new Image();
                img.src = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAAMElEQVQ4T2N89uzZfwY8QFJSEp80A+OoAcMiDP7//483HTx//hx/Ohg1gIFx6IcBALl+VXknOCvFAAAAAElFTkSuQmCC";
                const transparencyPattern = context.createPattern(img, 'repeat');
                context.globalCompositeOperation = 'destination-over';
                context.fillStyle = transparencyPattern;
                context.fillRect(0, 0, this.width, this.height);
                context.restore();
            },
            paintCroppedImage(context, image, horizontalBorder, verticalBorder) {
                if (!image.resource) return;

                const position = this.calculateInitialPosition(image, horizontalBorder, verticalBorder);
                context.save();
                context.globalCompositeOperation = 'destination-over';
                context.translate(this.width / 2, this.height/ 2);
                context.rotate(this.rotationRadian);
                context.translate(-this.width / 2, -this.height / 2);

                context.drawImage(
                    image.resource,
                    position.x - horizontalBorder,
                    position.y - verticalBorder,
                    position.width,
                    position.height);

                context.restore();
            },
            drawRoundedRect(context, x, y, width, height, borderRadius) {
                if (borderRadius === 0) {
                    context.rect(x, y, width, height);
                } else {
                    const widthMinusRad = width - borderRadius;
                    const heightMinusRad = height - borderRadius;

                    context.translate(x, y);
                    context.arc(borderRadius, borderRadius, borderRadius, Math.PI, Math.PI * 1.5);
                    context.lineTo(widthMinusRad, 0);
                    context.arc(widthMinusRad, borderRadius, borderRadius, Math.PI * 1.5, Math.PI * 2);
                    context.lineTo(width, heightMinusRad);
                    context.arc(widthMinusRad, heightMinusRad, borderRadius, Math.PI * 2, Math.PI * 0.5);
                    context.lineTo(borderRadius, height);
                    context.arc(borderRadius, heightMinusRad, borderRadius, Math.PI * 0.5, Math.PI);
                    context.translate(-x, -y);
                }
            },
            replaceImageInBounds() {
                const imageState = this.cropper.state.image;
                imageState.y = this.getBoundedY(imageState.y, this.local.scale);
                imageState.x = this.getBoundedX(imageState.x, this.local.scale);
            },
            calculateInitialPosition(image, horizontalBorder, verticalBorder) {
                image = image || this.cropper.state.image;

                const width = image.width * this.local.scale;
                const height = image.height * this.local.scale;

                let x = image.x * this.local.scale;
                let y = image.y * this.local.scale;

                [x, y] = this.transformDataWithRotation(x, y);
                x += (this.width - width) / 2;
                y += (this.height - height) / 2;

                return {
                    x, 
                    y,
                    height,
                    width
                };
            },
            transformDataWithRotation(x, y) {
                const radian = -this.rotationRadian;
                const rx = x * Math.cos(radian) - y * Math.sin(radian);
                const ry = x * Math.sin(radian) + y * Math.cos(radian);

                return [rx, ry];
            },
            getBoundedX(x, scale) {
                const image = this.cropper.state.image;
                const width = Math.abs(image.width * Math.cos(this.rotationRadian)) + Math.abs(image.height * Math.sin(this.rotationRadian));
                let widthDiff = Math.floor((width - (this.width - (this.horizontalBorder * 2)) / scale) / 2);
                widthDiff = Math.max(0, widthDiff);
                return Math.max(-widthDiff, Math.min(x, widthDiff));
            },
            getBoundedY(y, scale) {
                const image = this.cropper.state.image;
                const height = Math.abs(image.width * Math.sin(this.rotationRadian)) + Math.abs(image.height * Math.cos(this.rotationRadian));
                let heightDiff = Math.floor((height - (this.height - (this.verticalBorder * 2)) / scale) / 2);
                heightDiff = Math.max(0, heightDiff);
                return Math.max(-heightDiff, Math.min(y, heightDiff));
            },
            getInitialSize(width, height) {
                let newHeight;
                let newWidth;

                const viewportWidth = this.width - (this.horizontalBorder * 2);
                const viewportHeight = this.height - (this.verticalBorder * 2);
                
                if (width < viewportWidth && height < viewportHeight) {
                    return {
                        height,
                        width
                    };
                }

                const viewportRatio = viewportHeight / viewportWidth;
                const imageRatio = height / width;

                if (viewportRatio > imageRatio) {
                    newHeight = (viewportHeight);
                    newWidth = (width * (newHeight / height));
                } else {
                    newWidth = (viewportWidth);
                    newHeight = (height * (newWidth / width));
                }

                return {
                    height: newHeight,
                    width: newWidth
                };
            },
            getScaledImageDataUrl() {

                const canvas = document.createElement('canvas');
                canvas.width = this.width - (this.horizontalBorder * 2);
                canvas.height = this.height - (this.verticalBorder * 2);

                this.paintCroppedImage(canvas.getContext('2d'), this.cropper.state.image, this.horizontalBorder, this.verticalBorder);

                return canvas.toDataURL();
            },

            // Helpers
            isDataUrl(value) {
                if (value === null) {
                    return false;
                }
                return !!value.match(/^\s*data:([a-z]+\/[a-z]+(;[a-z\-]+=[a-z\-]+)?)?(;base64)?,[a-z0-9!$&',()*+;=\-._~:@\/?%\s]*\s*$/i);
            },

            // Event handlers
            onFileAdded(file) {
                return this.getDataUrl(file).then((dataUrl) => {
                    this.local.src = dataUrl;

                    this.$emit('upload', {
                        file,
                        dataUrl: this.local.src
                    });
                });
            },
            onCropperDragStart(e) {
                e = e || window.event;
                e.preventDefault();

                this.cropper.state.cursor = 'grabbing';
                this.cropper.state.drag = true;
                this.cropper.state.mx = null;
                this.cropper.state.my = null;

                const eventSubject = document;
                let hasMoved = false;
                const handleMouseUp = (event) => {
                    this.onCropperDragEnd(event);
                    if (!hasMoved && event.targetTouches) {
                        e.target.click();
                    }
                    eventSubject.removeEventListener('mouseup', handleMouseUp);
                    eventSubject.removeEventListener('mousemove', handleMouseMove);
                    eventSubject.removeEventListener('touchend', handleMouseUp);
                    eventSubject.removeEventListener('touchmove', handleMouseMove);
                };
                const handleMouseMove = (event) => {
                    hasMoved = true;
                    this.onCropperMouseMove(event);
                };

                eventSubject.addEventListener('mouseup', handleMouseUp);
                eventSubject.addEventListener('mousemove', handleMouseMove);
                eventSubject.addEventListener('touchend', handleMouseUp);
                eventSubject.addEventListener('touchmove', handleMouseMove);
            },
            onCropperDragEnd(e) {
                if (this.cropper.state.drag) {
                    this.cropper.state.drag = false;
                    this.cropper.state.cursor = 'grab';
                }
            },
            onCropperMouseMove(e) {
                e = e || window.event;

                const state = this.cropper.state;

                if (state.drag === false) {
                    return;
                }

                const imageState = state.image;
                const lastX = imageState.x;
                const lastY = imageState.y;

                const mousePositionX = e.targetTouches ? e.targetTouches[0].pageX : e.clientX;
                const mousePositionY = e.targetTouches ? e.targetTouches[0].pageY : e.clientY;

                const newState = {
                    mx: mousePositionX,
                    my: mousePositionY,
                    image: imageState
                };

                if (state.mx && state.my) {
                    const xDiff = (state.mx - mousePositionX) / this.local.scale;
                    const yDiff = (state.my - mousePositionY) / this.local.scale;

                    imageState.y = this.getBoundedY(lastY - yDiff, this.local.scale);
                    imageState.x = this.getBoundedX(lastX - xDiff, this.local.scale);
                }

                state.mx = newState.mx;
                state.my = newState.my;
                state.image = imageState;
            },
            onCropperWheel(e) {
                e.preventDefault();

                let deltaY = 0;

			    if (e.deltaY) {
				    deltaY = e.deltaY;
			    } else if (e.wheelDelta) {
				    deltaY = -e.wheelDelta;
                }

                this.zoom(deltaY < 0 ? 1 : -1);
            },
            emitChange() {
                this.$nextTick().then(() => {
                    this.$nextTick(() => {
                        this.$emit('change', this.getScaledImageDataUrl());
                    });
                });
            }
        }
	};
</script>

<style lang="scss">
@import '~@/assets/styles/references';


.image-upload {
    /* Vue-transmit styles */
    .v-transmit__upload-area {  }
    .v-transmit__upload-area--is-dragging {
        .iu-uploader { background: repeating-linear-gradient(-45deg, rgba(0,0,0,0.08), rgba(0,0,0,0.08) 8px, color(black, state-bg) 8px, color(black, state-bg) 16px); }
    }
}

.iu-uploader { min-height: 140px; background-color: color(black, state-bg); border: 1px dashed color(black, divider); border-radius: $border-radius; padding: gx(1); display: flex; flex-direction: column; align-items: center; justify-content: center; }
.iu-uploader-icon { color: color(black, hint);
    .icon { font-size: 40px; }
}
.iu-uploader-body { color: color(black, primary); text-align: center; }
.iu-uploader-instructions-title { font-size: $font-size-base; font-weight: $font-weight-medium; margin-bottom: gx(0.25); }
.iu-uploader-instructions-subtitle { font-size: $font-size-xs; color: color(black, secondary); }

.iu-cursor-default { cursor: default; }
.iu-cursor-pointer { cursor: pointer; }
.iu-cursor-grab { cursor: grab; cursor: -webkit-grab; cursor: -moz-grab; }
.iu-cursor-grabbing { cursor: grabbing; cursor: -webkit-grabbing; cursor: -moz-grabbing; }

</style>
